import { create } from 'zustand'
import { supabase, type GameSave } from '../lib/supabase'
import { useAuthStore } from './authStore'
import type { SaveSlotInfo } from './saveStore'

// 云端存档状态
export interface CloudSaveState {
  // 云端存档列表
  cloudSaves: GameSave[]
  isLoading: boolean
  error: string | null
  lastSyncAt: number | null

  // 操作
  fetchCloudSaves: () => Promise<void>
  uploadToCloud: (slotIndex: number, localSave: SaveSlotInfo) => Promise<boolean>
  downloadFromCloud: (cloudSave: GameSave) => Promise<SaveSlotInfo | null>
  deleteCloudSave: (saveId: string) => Promise<boolean>
  clearError: () => void
}

export const useCloudSaveStore = create<CloudSaveState>()((set, get) => ({
  // 初始状态
  cloudSaves: [],
  isLoading: false,
  error: null,
  lastSyncAt: null,

  // 获取云端存档列表
  fetchCloudSaves: async () => {
    const user = useAuthStore.getState().user
    if (!user) {
      set({ error: '请先登录' })
      return
    }

    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase
        .from('game_saves')
        .select('*')
        .order('slot_number', { ascending: true })

      if (error) {
        set({ error: error.message, isLoading: false })
        return
      }

      set({
        cloudSaves: data || [],
        isLoading: false,
        lastSyncAt: Date.now(),
      })
    } catch (err) {
      set({ error: '获取云端存档失败', isLoading: false })
    }
  },

  // 上传到云端
  uploadToCloud: async (slotIndex, localSave) => {
    const user = useAuthStore.getState().user
    if (!user) {
      set({ error: '请先登录' })
      return false
    }

    set({ isLoading: true, error: null })

    try {
      // 检查该槽位是否已有云端存档
      const existingSave = get().cloudSaves.find(
        (s) => s.slot_number === slotIndex + 1
      )

      const saveData = {
        user_id: user.id,
        slot_number: slotIndex + 1,
        save_name: localSave.name,
        play_time_seconds: localSave.playTimeSeconds,
        current_year: localSave.gameYear,
        current_month: localSave.gameMonth,
        preview: {
          countryName: '诸侯国',
          totalPopulation: localSave.preview.totalPopulation,
          totalMoney: localSave.preview.totalMoney,
          talentCount: localSave.preview.talentCount,
        },
        game_data: localSave.gameData,
        version: 1,
      }

      if (existingSave) {
        // 更新现有存档
        const { error } = await supabase
          .from('game_saves')
          .update(saveData)
          .eq('id', existingSave.id)

        if (error) {
          set({ error: error.message, isLoading: false })
          return false
        }
      } else {
        // 创建新存档
        const { error } = await supabase.from('game_saves').insert(saveData)

        if (error) {
          set({ error: error.message, isLoading: false })
          return false
        }
      }

      // 刷新云端存档列表
      await get().fetchCloudSaves()
      set({ isLoading: false })
      return true
    } catch (err) {
      set({ error: '上传失败，请重试', isLoading: false })
      return false
    }
  },

  // 从云端下载
  downloadFromCloud: async (cloudSave) => {
    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase
        .from('game_saves')
        .select('*')
        .eq('id', cloudSave.id)
        .single()

      if (error || !data) {
        set({ error: error?.message || '下载失败', isLoading: false })
        return null
      }

      // 转换为本地存档格式
      const localSave: SaveSlotInfo = {
        id: data.id,
        name: data.save_name,
        createdAt: new Date(data.created_at).getTime(),
        updatedAt: new Date(data.updated_at).getTime(),
        playTimeSeconds: data.play_time_seconds,
        gameYear: data.current_year,
        gameMonth: data.current_month,
        preview: {
          totalPopulation: data.preview.totalPopulation || 0,
          totalMoney: data.preview.totalMoney || 0,
          totalFood: 0, // 云端可能没有这个字段
          talentCount: data.preview.talentCount || 0,
          buildingCount: 0,
        },
        gameData: data.game_data as SaveSlotInfo['gameData'],
      }

      set({ isLoading: false })
      return localSave
    } catch (err) {
      set({ error: '下载失败，请重试', isLoading: false })
      return null
    }
  },

  // 删除云端存档
  deleteCloudSave: async (saveId) => {
    set({ isLoading: true, error: null })

    try {
      const { error } = await supabase
        .from('game_saves')
        .delete()
        .eq('id', saveId)

      if (error) {
        set({ error: error.message, isLoading: false })
        return false
      }

      // 刷新列表
      await get().fetchCloudSaves()
      set({ isLoading: false })
      return true
    } catch (err) {
      set({ error: '删除失败，请重试', isLoading: false })
      return false
    }
  },

  // 清除错误
  clearError: () => set({ error: null }),
}))
